import { h } from '@atomify/jsx';

import { ButtonLoader } from '../button-loader';
import { BPDFunctionalSvg } from '../svg/svg';

export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'grey-90'
    | 'grey-10'
    | 'grey-5'
    | 'white';

export type ButtonWidth = 'square' | 'full-width' | 'full-width-mobile';

export type ButtonSize = 'large' | 'small' | 'regular';

export type ButtonShape =
    | 'round'
    | 'smooth-32'
    | 'smooth-25'
    | 'smooth-15'
    | 'smooth-10'
    | 'smooth-5';

export interface ButtonInterface {
    label?: string | Text | null;
    variant?: ButtonVariant | null;
    width?: ButtonWidth | null;
    size?: ButtonSize | null;
    shape?: ButtonShape | null;
    icon?: string | null;
    classes?: string;
    type?: string;
    url?: string | null;
    hook?: string;
    controls?: string;
    onClick?: Function;
    children?: Element[];
    disabled?: boolean;
    hasLoading?: boolean;
    loadableModal?: string;
    attr?: {};
    element?: string;
}

const createClassName = (data: ButtonInterface) => {
    const variantClass = data.variant ? `c-button--${data.variant}` : 'c-button';

    return {
        [variantClass]: true,
        ...(data.icon && { ['button--icon']: true }),
        ...(data.width && { [`button--${data.width}`]: true }),
        ...(data.size && { [`button--${data.size}`]: true }),
        ...(data.shape && { [`button--${data.shape}`]: true }),
        ...(data.classes && { [data.classes]: true }),
    };
};

const createAttributes = (data: ButtonInterface) => {
    return {
        ...data.attr,
        type: data.type || 'button',
        ...(data.url && { href: data.url }),
        ...(data.onClick && { onClick: data.onClick }),
        ...(data.disabled && { disabled: data.disabled }),
        ...(data.hook && { [`js-hook-${data.hook}`]: data.hook }),
        ...(data.controls && { ['aria-controls']: data.controls }),
        ...(data.loadableModal && { ['js-hook-loadable-modal']: data.loadableModal }),
    };
};

const Button = (data: ButtonInterface) => {
    const className = createClassName(data);
    const attributes = createAttributes(data);

    const Tag = `${data.element || 'button'}`;
    const hasLabel = data.label !== null && data.label !== undefined;

    return (
        <Tag class={className} {...attributes}>
            {data.icon && (
                <span class="button__icon">
                    <BPDFunctionalSvg name={data.icon} />
                </span>
            )}

            {hasLabel || data.children?.length ? (
                <span class="button__label">
                    {data.label ?? ''}
                    {data.children ?? ''}
                </span>
            ) : null}

            {(data.hasLoading || data.loadableModal) && <ButtonLoader />}
        </Tag>
    );
};

const ButtonA = ({ children, ...data }: ButtonInterface) => (
    <Button {...data} element="a">
        {children}
    </Button>
);

const ButtonRound = ({ children, ...data }: ButtonInterface) => (
    <Button {...data} shape="round" width="square">
        {children}
    </Button>
);

export { Button, ButtonA, ButtonRound };
